import { useCallback, useEffect, useState } from 'react';
import { Controller, useForm } from 'react-hook-form';
import isEqual from 'lodash.isequal';
import {
  IconLoader,
  ButtonPrimary,
  ResponseError,
  responseErrorsResolver,
  ToggleInput,
  getResponseErrors,
} from '@ui/src';
import { useAppSelector } from '@store';
import { useLazyGetEmployeesTransactionsTypesQuery } from '../settings/store/services/transactions-types';
import { useLazyGetActiveCurrenciesQuery } from '../../store/dictionaries/service';
import {
  useEditBeneficiarySettingsMutation,
  useLazyGetPSPDetailsQuery,
} from '../settings/store/services/details';
import { TransactionsSettingsKeys } from '../settings/types';

import cn from '../settings/styles.module.css';

const Overdraft = () => {
  const [defaultFormValues, setDefaultFormValues] = useState<{
    overdraftEnabled: boolean;
    overdraftVisibleToClients: boolean;
  }>();

  const [getBeneficiariesSettings, { isLoading, isFetching }] =
    useLazyGetPSPDetailsQuery();
  const [edit, { isLoading: editIsLoading }] =
    useEditBeneficiarySettingsMutation();
  const [globalError, setGlobalError] = useState('');
  const [getTransactionTypesList, { isLoading: transactionTypesIsLoading }] =
    useLazyGetEmployeesTransactionsTypesQuery();
  const [getActiveCurrencies, { isLoading: activeCurrenciesIsLoading }] =
    useLazyGetActiveCurrenciesQuery();
  const [errorList, setErrorList] = useState('');

  const {
    settings: {
      details: { pspDetails },
      transactionsTypes: { values: transactionsTypes },
    },
    administrationDictionaries: { activeCurrencies },
  } = useAppSelector((state) => state);

  const {
    control,
    handleSubmit,
    reset,
    watch,
    setValue,
    setError,
    formState: { errors },
  } = useForm<{
    overdraftEnabled: boolean;
    overdraftVisibleToClients: boolean;
  }>();

  const fields = watch();

  const isDirty = !isEqual(fields, defaultFormValues);

  const onSubmit = handleSubmit(async (values) => {
    setGlobalError('');

    const updatedSettings: {
      [key: string]:
        | string
        | boolean
        | string[]
        | Record<string, string>
        | Record<string, boolean>;
      key: TransactionsSettingsKeys;
    }[] = [];

    if (values.overdraftEnabled !== defaultFormValues?.overdraftEnabled) {
      updatedSettings.push({
        overdraftEnabled: values.overdraftEnabled,
        key: TransactionsSettingsKeys.OVERDRAFT_ENABLED,
      });
    }

    if (
      values.overdraftVisibleToClients !==
      defaultFormValues?.overdraftVisibleToClients
    ) {
      updatedSettings.push({
        overdraftVisibleToClients: values.overdraftVisibleToClients,
        key: TransactionsSettingsKeys.OVERDRAFT_VISIBLE_TO_CLIENTS,
      });
    }

    if (updatedSettings.length > 0) {
      const response = (await Promise.all(
        updatedSettings.map(async (item) => {
          const res = await edit(item);

          return res;
        }),
      )) as { error?: ResponseError }[];

      if (response.find((item) => item.error)) {
        const errorsResponse = response.filter((item) => item.error);

        errorsResponse.forEach((item) => {
          responseErrorsResolver({
            errors: item.error as ResponseError,
            setError,
            setGlobalError,
          });
        });
      }

      const body: {
        overdraftEnabled: boolean;
        overdraftVisibleToClients: boolean;
      } = {
        overdraftEnabled: values.overdraftEnabled,
        overdraftVisibleToClients: values.overdraftVisibleToClients,
      };

      setDefaultFormValues(body);
    }
  });

  const getBeneficiariesSettingsAction = useCallback(async () => {
    const response = (await getBeneficiariesSettings()) as {
      error?: ResponseError;
    };

    if (response?.error) {
      getResponseErrors({
        error: response?.error,
        setErrorList,
      });
    }
  }, []);

  useEffect(() => {
    if (!transactionsTypes) {
      getTransactionTypesList();
    }

    if (!activeCurrencies) {
      getActiveCurrencies();
    }
  }, [transactionsTypes, activeCurrencies]);

  useEffect(() => {
    if (!pspDetails) {
      getBeneficiariesSettingsAction();
    }
  }, [pspDetails]);

  useEffect(() => {
    if (pspDetails) {
      const overdraftEnabled = pspDetails?.find(
        (item) => item.key === TransactionsSettingsKeys.OVERDRAFT_ENABLED,
      )?.data?.value as boolean;

      const overdraftVisibleToClients = pspDetails?.find(
        (item) =>
          item.key === TransactionsSettingsKeys.OVERDRAFT_VISIBLE_TO_CLIENTS,
      )?.data?.value as boolean;

      const body: {
        overdraftEnabled: boolean;
        overdraftVisibleToClients: boolean;
      } = {
        overdraftEnabled,
        overdraftVisibleToClients,
      };

      reset(body);
      setDefaultFormValues(body);
    }
  }, [pspDetails]);

  useEffect(() => {
    let title = 'Transaction Settings: Overdraft';

    const companyName = localStorage.getItem('companyName');

    if (companyName) {
      title = `${title} | ${companyName}`;
    }

    document.title = title;
  }, []);

  let content;

  if (
    transactionTypesIsLoading ||
    activeCurrenciesIsLoading ||
    isLoading ||
    isFetching
  ) {
    content = (
      <div className={cn.beneficiariesSettingsLoader}>
        <IconLoader />
      </div>
    );
  } else {
    content = (
      <div className={cn.beneficiariesSettingsContent}>
        {!errorList && (
          <>
            <div className={cn.beneficiariesSettingsBlock}>
              <div className={cn.beneficiariesSettingsTitle}>
                Overdraft availability
              </div>
            </div>
            <div className={cn.transactionOptionsBlock}>
              <div className={cn.formGroup}>
                <Controller
                  control={control}
                  name="overdraftEnabled"
                  render={({ field: { value, name, onChange } }) => (
                    <>
                      <ToggleInput
                        name={name}
                        title="Using an Overdraft"
                        onChange={(e) => {
                          onChange(e.target.checked);

                          if (!e.target.checked) {
                            setValue('overdraftVisibleToClients', false);
                          }
                        }}
                        checked={value}
                      />
                      {errors?.overdraftEnabled?.message && (
                        <div className={cn.settingError}>
                          {errors?.overdraftEnabled?.message}
                        </div>
                      )}
                    </>
                  )}
                />
              </div>
              {fields.overdraftEnabled && (
                <div className={cn.formGroup}>
                  <Controller
                    control={control}
                    name="overdraftVisibleToClients"
                    render={({ field: { value, name, onChange } }) => (
                      <>
                        <ToggleInput
                          name={name}
                          title="Show overdraft amount to clients"
                          onChange={(e) => {
                            onChange(e.target.checked);
                          }}
                          checked={value}
                        />
                        {errors?.overdraftVisibleToClients?.message && (
                          <div className={cn.settingError}>
                            {errors?.overdraftVisibleToClients?.message}
                          </div>
                        )}
                      </>
                    )}
                  />
                </div>
              )}
            </div>

            {globalError && (
              <div className={cn.saveGlobalError}>{globalError}</div>
            )}
            <ButtonPrimary
              text={editIsLoading ? 'Loading...' : 'Save'}
              className={cn.saveBeneficiariesSettings}
              disabled={!isDirty || editIsLoading}
              onClick={onSubmit}
              bold
            />
          </>
        )}
        {errorList && (
          <div className={cn.loadingError}>
            <div className={cn.loadingErrorTitle}>Unable to load data</div>
            <div className={cn.loadingErrorText}>{errorList}</div>
          </div>
        )}
      </div>
    );
  }

  return <div className={cn.beneficiariesSettingsWrapper}>{content}</div>;
};

export default Overdraft;
